import db07Table from '../../assets/json/db07Table.json';
import pricesPainter from '../../assets/json/painterPricesConfig.json';
import pricesCleaner from '../../assets/json/cleanerPricesConfig.json';
import pricesCarpenter from '../../assets/json/carpenterPricesConfig.json';
import pricesBrickbuilder from '../../assets/json/brickbuilderPricesConfig.json';
import pricesOfficeWorker from '../../assets/json/officeWorkerPricesConfig.json';
import { BUSINESS_LINES, ICoverage } from '../../common/interface/IPayload';
import { coverageName, ICvrData } from '../../common/interface/IAppProvider';

// Perhpas this function can be removed. Think that employees is always 1,2,3,4,5 at this point...
export const roundEmployees = (employees: number) => {
    let r = Math.ceil(employees);
    if (r < 1) {
        r = 1;
    }
    return r;
};

export function clamp(n: number, min: number, max: number) {
    return Math.max(min, Math.min(n, max));
}

// Discount given depending on lockinperiod(løbetid).
const lockinPeriodDiscount = [
    { value: '1', discount: 1 }, // no discount
    { value: '3', discount: 0.9 }, // 10% discount
    { value: '5', discount: 0.85 }, // 15% discount
];

export const priceConfig = [
    { line: BUSINESS_LINES.PAINTER, priceConfig: pricesPainter },
    { line: BUSINESS_LINES.BRICKBUILDER, priceConfig: pricesBrickbuilder },
    { line: BUSINESS_LINES.CARPENTER, priceConfig: pricesCarpenter },
    { line: BUSINESS_LINES.CLEANER, priceConfig: pricesCleaner },
];

export const turnoverLevels = [1000000, 2000000, 3000000, 4000000, 5000000];

export function turnoverIndex(turnover: number | undefined) {
    if (turnover === undefined) {
        turnover = 0;
    }
    let index = -1;
    for (let i = 0; i < turnoverLevels.length; i++) {
        let min = turnoverLevels[i - 1] || Number.NEGATIVE_INFINITY;
        let max = turnoverLevels[i];
        if (turnover > min && turnover <= max) {
            index = i;
        }
    }
    if (index === -1) {
        throw new Error(`Turnover to large: ${turnover}`);
    }
    return index;
}

const getFinalPrice = (price: number, lockPeriod: string) => {
    const discount = lockinPeriodDiscount.find((x) => x.value === lockPeriod)?.discount ?? 1;
    return price * discount;
};

export const getCoverages = (
    businessLine: string,
    packageName: string,
    isHideOccupationalInjury: boolean,
): ICoverage[] => {
    let priceLineConfig = priceConfig.find((x) => x.line === businessLine)?.priceConfig || [];
    if (isHideOccupationalInjury) {
        priceLineConfig = priceLineConfig.filter((config) => config.label !== 'occupational-injury');
    }
    let covs: any[] = [];
    priceLineConfig.forEach((config) => {
        const isCoverageInPackage = !!config.packages.find((pckg) => pckg === packageName);
        covs = [...covs, { name: config.text, label: config.label, selected: isCoverageInPackage }];
    });
    return covs;
};

export const getCoveragesDetails = (businessLine: string, isHideOccupationalInjury: boolean) => {
    let priceLineConfig = priceConfig.find((x) => x.line === businessLine)?.priceConfig || [];
    let coverageDetails: any[] = [];
    if (isHideOccupationalInjury) {
        priceLineConfig = priceLineConfig.filter((config) => config.label !== 'occupational-injury');
    }
    priceLineConfig.forEach((data) => {
        coverageDetails = [...coverageDetails, { name: data.text, label: data.label, packages: data.packages }];
    });
    return coverageDetails;
};

const getPriceIndex = (priceArray: any, indexE: number, indexT: number) => {
    switch (priceArray.text) {
        case coverageName.INJURY: {
            return indexE;
        }
        case coverageName.LEGAL:
        case coverageName.TRANSPORT: {
            return indexT;
        }
        default: {
            return Math.max(indexE, indexT);
        }
    }
};

export function calculatePrices(
    employees: number,
    turnover: number | undefined,
    businessLine: string,
    lockinPeriod: string,
    isHideOccupationalInjury: boolean,
) {
    const indexE = roundEmployees(employees) - 1;
    const indexT = turnoverIndex(turnover);
    let prices = [0, 0, 0]; // extended, standard, basic
    let priceLineConfig = priceConfig.find((x) => x.line === businessLine)?.priceConfig;

    if (priceLineConfig) {
        if (isHideOccupationalInjury) {
            priceLineConfig = priceLineConfig.filter((config) => config.label !== 'occupational-injury');
        }
        priceLineConfig.forEach((priceArray) => {
            for (let i = 0; i < 3; i++) {
                const p = priceArray.price[i];
                if (p === null) {
                    // empty
                }
                if (typeof p === 'number') {
                    prices[i] += p;
                }
                if (Array.isArray(p)) {
                    const index = getPriceIndex(priceArray, indexE, indexT);
                    prices[i] += p[index];
                }
            }
        });
        prices[0] = Math.round(getFinalPrice(prices[0], lockinPeriod) / 12);
        prices[1] = Math.round(getFinalPrice(prices[1], lockinPeriod) / 12);
        prices[2] = Math.round(getFinalPrice(prices[2], lockinPeriod) / 12);
    }
    return prices;
}

export function getBusinessLine(companyType: string) {
    return db07Table.find((x) => x.db07 === companyType)?.line || '';
}

// Is the given companyType allowed to go get a price / go through the flow?
export function companyTypeAllowed(companyType: string) {
    return db07Table.find((x) => x.db07 === companyType) !== undefined;
}

// Is the given companyType allowed to get the Prof coverage?
export function profAllowed(companyType: string) {
    const b = db07Table.find((x) => x.db07 === companyType);
    if (b) {
        return b.risk !== 0;
    } else {
        throw new Error(`DB07 not found. ${companyType}`);
    }
}

// What "category" is the given companyType? Used to select what text to show the customer.
export function getCategory(companyType: string) {
    const b = db07Table.find((x) => x.db07 === companyType);
    if (!b) {
        throw new Error(`DB07 not found. ${companyType}`);
    } else {
        return b.cat;
    }
}

// Price of base package.
export function calcPackagePrice(employees: number, lockinPeriod: string, isHideOccupationalInjury: boolean) {
    const index = roundEmployees(employees) - 1;
    const prices = isHideOccupationalInjury
        ? pricesOfficeWorker.withoutOccupationalInjury
        : pricesOfficeWorker.withOccupationalInjury;
    const pppm = prices[index].pricePerMonth;
    return Math.round(getFinalPrice(pppm, lockinPeriod));
}

// Price Professional Coverage.
export function calcProfPrice(turnover: number | undefined, companyType: string, lockinPeriod: string) {
    turnover = turnover ? turnover : 0;
    const b = db07Table.find((x) => x.db07 === companyType);
    if (b?.min) {
        const risk = b.risk;
        let profPricePerYear = (turnover / 1000) * risk;
        const min = b.min;
        // Price can never go below min, so pick the larger one
        profPricePerYear = Math.max(profPricePerYear, min);
        return Math.round(getFinalPrice(profPricePerYear, lockinPeriod) / 12);
    } else {
        throw new Error(`DB07 not found. ${companyType}`);
    }
}

export function calculateCyberPrice(turnover: number | undefined, employees: number, lockinPeriod: string) {
    const cyberPricesPerMonth = [106, 109, 115, 121, 127];
    turnover = turnover ?? 0;
    let turnoverIdx = 0;
    while (turnover >= turnoverLevels[turnoverIdx + 1]) {
        turnoverIdx++;
    }

    let employeeIndex = roundEmployees(employees) - 1;

    let index = Math.max(turnoverIdx, employeeIndex);
    index = clamp(index, 0, 4);
    return Math.round(getFinalPrice(cyberPricesPerMonth[index], lockinPeriod));
}

export function calculateGdprPrice(turnover: number | undefined, employees: number, lockinPeriod: string) {
    const gdprPricesPerMonth = [95, 95, 95, 95, 95];
    turnover = turnover ?? 0;
    let turnoverIdx = 0;
    while (turnover >= turnoverLevels[turnoverIdx + 1]) {
        turnoverIdx++;
    }

    let employeeIndex = roundEmployees(employees) - 1;

    let index = Math.max(turnoverIdx, employeeIndex);
    index = clamp(index, 0, 4);
    return Math.round(getFinalPrice(gdprPricesPerMonth[index], lockinPeriod));
}

export function calculateOccupationalInjuryPrice(cvr: ICvrData, lockinPeriod: string) {
    if (cvr.businessLine === BUSINESS_LINES.OFFICE_WORK) {
        const price = db07Table.find((x) => x.db07 === cvr.business.key)?.injury?.sum!;
        return Math.round(getFinalPrice(price, lockinPeriod) / 12);
    } else {
        const priceLineConfig = priceConfig.find((x) => x.line === cvr.businessLine)?.priceConfig;
        const injuryPrices = priceLineConfig?.find((x) => x.text === coverageName.INJURY)?.price[0] ?? [];
        return Array.isArray(injuryPrices) ? Math.round(getFinalPrice(injuryPrices[0], lockinPeriod) / 12) : 0;
    }
}
